import { useTranslation } from "@hireroo/i18n";
import { ScoringPrecisionFeedbackForm } from "@hireroo/validator";
import { zodResolver } from "@hookform/resolvers/zod/dist/zod";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import Typography, { TypographyProps } from "@mui/material/Typography";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

import ResourceFieldV2 from "../../../modules/ResourceFieldV2/ResourceFieldV2";
import DialogWithCloseIcon, { DialogWithCloseIconProps } from "../../../primitive/DialogWithCloseIcon/DialogWithCloseIcon";
import InputControlTextField, { InputControlTextFieldProps } from "../../../primitive/InputControl/InputControlTextField/InputControlTextField";
import RadioControlField, { RadioControlFieldProps } from "../../../primitive/InputControl/RadioControlField/RadioControlField";
import FeedbackFollowUpContent from "./parts/FeedbackFollowUpContent/FeedbackFollowUpContent";

const NoticeTextTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: "12px",
  marginBottom: theme.spacing(3),
}));

const StyledDialogWithCloseIcon = styled(DialogWithCloseIcon)(() => ({
  ".MuiDialog-paper": {
    position: "fixed",
    right: 0,
    bottom: 0,
    maxHeight: "560px",
    width: "444px",
    minWidth: "444px",
  },
}));

type Option = {
  id: ScoringPrecisionFeedbackForm.FeedbackAnswer;
  label: string;
  value: ScoringPrecisionFeedbackForm.FeedbackAnswer;
};

type DialogController = {
  setLoading: (loading: boolean) => void;
  close: () => void;
};

export type ScoringFeedbackDialogProps = {
  dialog: Pick<DialogWithCloseIconProps, "open" | "onClose"> & { closeText?: string };
  onSubmit: (fields: ScoringPrecisionFeedbackForm.ScoringPrecisionFeedbackFormSchema, controller: DialogController) => void;
};

const ScoringFeedbackDialog: React.FC<ScoringFeedbackDialogProps> = props => {
  const { t } = useTranslation();
  const answerValidateSchema = ScoringPrecisionFeedbackForm.useFeedbackAnswer();
  const validateSchema = ScoringPrecisionFeedbackForm.useScoringPrecisionFeedbackFormSchema();
  const methods = useForm<ScoringPrecisionFeedbackForm.ScoringPrecisionFeedbackFormSchema>({
    resolver: zodResolver(validateSchema),
    defaultValues: {
      answer: undefined,
      comment: undefined,
      entityTypes: [],
      categoryReasons: [],
      unclearCriteriaReasons: [],
    },
  });
  const [loading, setLoading] = React.useState(false);

  const handleClose = () => {
    props.dialog.onClose?.();
    setLoading(false);
  };

  const dialogController: DialogController = {
    setLoading: newLoading => {
      setLoading(newLoading);
    },
    close: () => {
      handleClose();
    },
  };
  const canSubmit = methods.watch("answer") !== undefined;

  const dialog: DialogWithCloseIconProps = {
    ...props.dialog,
    title: t("フィードバック"),
    yesButton: {
      children: t("送信する"),
      onClick: () => {
        methods.handleSubmit(fields => {
          props.onSubmit(fields, dialogController);
        })();
      },
      disabled: loading || !canSubmit,
      color: "secondary",
      autoFocus: false,
    },
    yesButtonTooltip: !canSubmit ? { title: t("回答を選択してください") } : undefined,
    noButton: {
      children: props.dialog.closeText ?? t("キャンセル"),
      variant: "outlined",
      color: "outline-only",
      disabled: loading,
      onClick: handleClose,
    },
    disableEnter: true,
    optionalDialog: {
      disableAutoFocus: true,
    },
  };
  const titleTypographyProps: TypographyProps = {
    fontSize: "16px",
    fontWeight: 700,
    mt: 2,
    mb: 3,
    children: t("レポートのスコアは参考になりましたか？"),
  };

  const options: Option[] = [
    {
      id: "YES",
      label: t("参考になった"),
      value: "YES",
    },
    {
      id: "NO",
      label: t("あまり参考にならなかった"),
      value: "NO",
    },
    {
      id: "NOT_SURE",
      label: t("わからない"),
      value: "NOT_SURE",
    },
  ];
  const answer = methods.watch("answer");

  const answerPlaceholderMap: Record<ScoringPrecisionFeedbackForm.FeedbackAnswer, string> = {
    YES: t("〇〇の指標が参考になった"),
    NO: t("〇〇の指標が参考にならなかった\n〇〇の観点を重視しており、このレポートからは判断がつかなかった"),
    NOT_SURE: t("わからない理由を具体的に記述してください。"),
  };

  const radioControlFieldProps: RadioControlFieldProps = {
    name: "answer",
    fullWidth: true,
    color: "secondary",
    size: "medium",
    options: options,
    onChange: value => {
      const answer = answerValidateSchema.safeParse(value);
      if (answer.success) {
        methods.setValue("answer", answer.data);
      }
    },
  };

  const textFieldProps: InputControlTextFieldProps = {
    fullWidth: true,
    multiline: true,
    rows: 4,
    type: "text",
    placeholder: answerPlaceholderMap[answer],
  };

  return (
    <StyledDialogWithCloseIcon {...dialog}>
      <FormProvider {...methods}>
        <NoticeTextTypography>
          {t("このフィードバックはスコアの精度向上に役立てるもので、候補者の評価とは無関係となります。")}
        </NoticeTextTypography>
        <Typography {...titleTypographyProps} />
        <Box pb={3}>
          <RadioControlField {...radioControlFieldProps} />
        </Box>
        {answer === "NO" && <FeedbackFollowUpContent />}

        <ResourceFieldV2
          label={t("上記を選んだ理由や背景を、具体的に教えてください。")}
          kind="NONE"
          help={{
            kind: "DESCRIPTION",
            text: t("その他、ご意見・ご要望があれば自由にお書きください。") + t("機能改善にご協力お願いします。"),
          }}
          formLabel={{
            sx: {
              width: "100%",
              whiteSpace: "pre",
            },
          }}
        >
          <InputControlTextField name="comment" {...textFieldProps} />
        </ResourceFieldV2>
      </FormProvider>
    </StyledDialogWithCloseIcon>
  );
};

ScoringFeedbackDialog.displayName = "ScoringFeedbackDialog";

export default ScoringFeedbackDialog;
